import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CffCreditNotes,
  CffRequestType,
  CustomerFeedbackFormData,
  CustomerFeedbackItemRow,
} from '../../types/applicationForm';
import {
  lookupCffInvoice,
  searchCffCustomers,
  searchCffItems,
  type CffCustomerLookup,
  type CffItemLookup,
} from '../../lib/customerFeedbackLookup';
import {
  fetchCustomerFeedbackSubmissions,
  findCffDuplicateInvoiceNcpars,
} from '../../lib/customerFeedbackSubmissions';
import {
  fetchFormFieldOptions,
  type FormFieldOption,
} from '../../lib/applicationFormSettings';
import { CffAsyncTypeahead, type CffAsyncOption } from './CffAsyncTypeahead';
import { CffSearchableSelect } from './CffSearchableSelect';

type CustomerFeedbackFormProps = {
  value: CustomerFeedbackFormData;
  onChange: (next: CustomerFeedbackFormData) => void;
  applicationNo?: string;
  /** Current submission id — excluded from duplicate invoice checks when editing. */
  submissionId?: string;
  /** Locked display name for Submitted By. */
  submittedByDisplayName: string;
  disabled?: boolean;
  /** When the form is disabled, still allow editing Internal remarks. */
  allowInternalRemarks?: boolean;
  /** When the form is disabled, still allow editing Picker / Packer / Driver. */
  allowWarehouseRoles?: boolean;
  /** Called when Internal remarks or warehouse-role fields lose focus (e.g. auto-save). */
  onInternalRemarksBlur?: () => void;
};

function setItem(
  items: CustomerFeedbackItemRow[],
  index: number,
  patch: Partial<CustomerFeedbackItemRow>,
): CustomerFeedbackItemRow[] {
  return items.map((row, i) => (i === index ? { ...row, ...patch } : row));
}

function customerFromMeta(meta: unknown): CffCustomerLookup | null {
  if (!meta || typeof meta !== 'object') return null;
  return meta as CffCustomerLookup;
}

function itemFromMeta(meta: unknown): CffItemLookup | null {
  if (!meta || typeof meta !== 'object') return null;
  return meta as CffItemLookup;
}

function inputClass(readOnly: boolean): string {
  return readOnly ? 'field-input field-input--readonly' : 'field-input';
}

export function CustomerFeedbackForm({
  value,
  onChange,
  applicationNo,
  submissionId,
  submittedByDisplayName,
  disabled = false,
  allowInternalRemarks = false,
  allowWarehouseRoles = false,
  onInternalRemarksBlur,
}: CustomerFeedbackFormProps) {
  const [customerLocked, setCustomerLocked] = useState(true);
  const [duplicateNcpars, setDuplicateNcpars] = useState<string[]>([]);
  const [invoiceLookupStatus, setInvoiceLookupStatus] = useState<
    'idle' | 'loading' | 'matched' | 'not_found' | 'error'
  >('idle');
  const [reasonOptions, setReasonOptions] = useState<FormFieldOption[]>([]);
  const lockedForRef = useRef<string | undefined>(undefined);
  const valueRef = useRef(value);
  valueRef.current = value;
  const lastInvoiceLookupRef = useRef('');
  const customerReadOnly = customerLocked || disabled;
  const remarksDisabled = disabled && !allowInternalRemarks;
  const warehouseRolesDisabled = disabled && !allowWarehouseRoles;

  useEffect(() => {
    const key = applicationNo ?? '__new__';
    if (lockedForRef.current === key) return;
    lockedForRef.current = key;
    // New form: allow company code search; invoice match still fills fields.
    setCustomerLocked(false);
  }, [applicationNo]);

  useEffect(() => {
    let cancelled = false;
    void fetchFormFieldOptions('customer-feedback')
      .then((rows) => {
        if (cancelled) return;
        setReasonOptions(
          rows
            .filter((r) => r.field_key === 'reason' && r.active)
            .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)),
        );
      })
      .catch(() => {
        if (!cancelled) setReasonOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const invoice = value.invoice_no.trim();
    if (!invoice) {
      setDuplicateNcpars([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchCustomerFeedbackSubmissions()
        .then((rows) => {
          if (cancelled) return;
          setDuplicateNcpars(
            findCffDuplicateInvoiceNcpars(rows, invoice, {
              excludeId: submissionId,
              excludeApplicationNo: applicationNo,
            }),
          );
        })
        .catch(() => {
          if (!cancelled) setDuplicateNcpars([]);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value.invoice_no, submissionId, applicationNo]);

  useEffect(() => {
    if (disabled) {
      setInvoiceLookupStatus('idle');
      return undefined;
    }
    const invoice = value.invoice_no.trim();
    if (!invoice) {
      setInvoiceLookupStatus('idle');
      lastInvoiceLookupRef.current = '';
      return undefined;
    }
    if (lastInvoiceLookupRef.current === invoice) {
      return undefined;
    }
    let cancelled = false;
    setInvoiceLookupStatus('loading');
    const timer = window.setTimeout(() => {
      void lookupCffInvoice(invoice)
        .then((match) => {
          if (cancelled) return;
          lastInvoiceLookupRef.current = invoice;
          if (!match) {
            setInvoiceLookupStatus('not_found');
            return;
          }
          const current = valueRef.current;
          if (current.invoice_no.trim() !== invoice) return;
          const nextDate = match.posting_date || current.invoice_date;
          const nextIssuedBy = match.invoice_issued_by || current.invoice_issued_by || '';
          const cust = match.customer;
          const next: CustomerFeedbackFormData = {
            ...current,
            invoice_date: nextDate,
            invoice_issued_by: nextIssuedBy,
            submitted_by: submittedByDisplayName,
          };
          if (cust?.no) {
            next.company_code = cust.no;
            if (cust.name) next.company_name = cust.name;
            if (cust.address) next.address = cust.address;
            if (cust.post_code) next.postal_code = cust.post_code;
            if (cust.territory_code) next.territory_code = cust.territory_code;
            if (cust.contact) next.contact_person = cust.contact;
            if (cust.phone_no) next.contact_number = cust.phone_no;
          }
          onChange(next);
          setInvoiceLookupStatus('matched');
        })
        .catch(() => {
          if (!cancelled) setInvoiceLookupStatus('error');
        });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value.invoice_no, disabled, onChange, submittedByDisplayName]);

  const patch = (partial: Partial<CustomerFeedbackFormData>) =>
    onChange({ ...valueRef.current, ...partial, submitted_by: submittedByDisplayName });

  // Section c: grey until lookup fails (manual entry) or a matched field is still empty.
  const invoiceDateFieldLocked =
    disabled ||
    !(
      invoiceLookupStatus === 'not_found' ||
      invoiceLookupStatus === 'error' ||
      (invoiceLookupStatus === 'matched' && !value.invoice_date)
    );
  const invoiceIssuedByFieldLocked =
    disabled ||
    !(
      invoiceLookupStatus === 'not_found' ||
      invoiceLookupStatus === 'error' ||
      (invoiceLookupStatus === 'matched' && !(value.invoice_issued_by || '').trim())
    );

  const searchCustomers = useCallback(async (query: string): Promise<CffAsyncOption[]> => {
    const rows = await searchCffCustomers(query);
    return rows.map((row) => ({
      value: row.no,
      label: row.name ? `${row.no} — ${row.name}` : row.no,
      meta: row,
    }));
  }, []);

  const searchItems = useCallback(async (query: string): Promise<CffAsyncOption[]> => {
    const rows = await searchCffItems(query);
    return rows.map((row) => ({
      value: row.no,
      label: row.description ? `${row.no} — ${row.description}` : row.no,
      meta: row,
    }));
  }, []);

  const applyCustomer = (customer: CffCustomerLookup) => {
    onChange({
      ...valueRef.current,
      company_code: customer.no,
      company_name: customer.name,
      address: customer.address,
      postal_code: customer.post_code,
      territory_code: customer.territory_code,
      contact_person: customer.contact,
      contact_number: customer.phone_no,
      submitted_by: submittedByDisplayName,
    });
  };

  return (
    <div className={`cff-form${disabled ? ' is-readonly' : ''}`}>
      <header className="cff-form__header">
        <h2 className="cff-form__title">WAREHOUSE/LOGISTICS - CUSTOMER FEEDBACK FORM</h2>
        <p className="cff-form__title-zh">货仓部/物流部 - 客户反馈表格</p>
        <p className="cff-form__iso">ISO 22000 / VERSION IV</p>
      </header>

      <div className="cff-form__top">
        <label className="field cff-form__request-type">
          <span className="field-label">Request type</span>
          <select
            className="field-input"
            value={value.request_type}
            disabled={disabled}
            onChange={(e) => {
              const request_type = e.target.value as CffRequestType;
              const credit_notes: CffCreditNotes =
                request_type === 'return'
                  ? 'required'
                  : request_type === 'exchange' || request_type === 'replenish'
                    ? 'not_required'
                    : value.credit_notes;
              patch({ request_type, credit_notes });
            }}
          >
            <option value="">Select…</option>
            <option value="return">Return 退货</option>
            <option value="exchange">Exchange 换货</option>
            <option value="replenish">Replenish 补货</option>
          </select>
        </label>
        <label className="field cff-form__date">
          <span className="field-label">Date</span>
          <input
            type="date"
            className="field-input"
            value={value.date}
            disabled={disabled}
            onChange={(e) => patch({ date: e.target.value })}
          />
        </label>
        <label className="field cff-form__ncpar">
          <span className="field-label">NCPAR No.</span>
          <input
            className={inputClass(true)}
            value={applicationNo ?? ''}
            readOnly
            placeholder="Assigned on save"
          />
        </label>
      </div>

      <div className="cff-form__invoice-credit-row">
        <label className="field">
          <span className="field-label">Invoice No.</span>
          <input
            className={`field-input${duplicateNcpars.length > 0 ? ' cff-form__invoice-input--dup' : ''}`}
            value={value.invoice_no}
            disabled={disabled}
            placeholder="e.g. INV-2609-00001"
            onChange={(e) => {
              lastInvoiceLookupRef.current = '';
              patch({ invoice_no: e.target.value });
            }}
            aria-invalid={duplicateNcpars.length > 0}
          />
          {!disabled && invoiceLookupStatus === 'loading' ? (
            <span className="field-hint" role="status">
              Matching invoice from ledger…
            </span>
          ) : null}
          {!disabled && invoiceLookupStatus === 'not_found' ? (
            <span className="field-hint cff-form__invoice-miss" role="status">
              Invoice not found — enter customer and date manually.
            </span>
          ) : null}
          {!disabled && invoiceLookupStatus === 'error' ? (
            <span className="field-hint cff-form__invoice-miss" role="status">
              Invoice lookup unavailable — enter customer and date manually.
            </span>
          ) : null}
        </label>
        <label className="field">
          <span className="field-label">Credit Notes</span>
          <select
            className="field-input"
            value={value.credit_notes}
            disabled={disabled}
            onChange={(e) => patch({ credit_notes: e.target.value as CffCreditNotes })}
          >
            <option value="">Select…</option>
            <option value="required">Required</option>
            <option value="not_required">Not Required</option>
          </select>
        </label>
      </div>
      {duplicateNcpars.length > 0 ? (
        <div className="cff-form__invoice-dup-banner" role="status">
          <span className="cff-form__invoice-dup-banner-title">Duplicate invoice</span>
          <span className="cff-form__invoice-dup-banner-body">
            Already used on {duplicateNcpars.join(', ')}. Please check before posting.
          </span>
        </div>
      ) : null}

      <h3 className="cff-form__section">Section 1: CSO FEEDBACK (客服部反馈)</h3>
      <h4 className="cff-form__subhead">a) Customer Information (客户资料)</h4>

      <div className="cff-form__customer-row">
        <label className="field cff-form__code-field">
          <span className="field-label">Company Code</span>
          {customerReadOnly ? (
            <input
              className={inputClass(true)}
              value={value.company_code}
              readOnly
              disabled={disabled}
            />
          ) : (
            <CffAsyncTypeahead
              value={value.company_code}
              onChange={(code) => patch({ company_code: code })}
              onSelect={(opt) => {
                const customer = customerFromMeta(opt.meta);
                if (customer) applyCustomer(customer);
              }}
              search={searchCustomers}
              placeholder="Search by code or name"
              ariaLabel="Company code"
            />
          )}
        </label>
        <label className="field cff-form__name-field">
          <span className="field-label">Company Name</span>
          <input
            className={inputClass(customerReadOnly)}
            value={value.company_name}
            readOnly={customerReadOnly}
            required
            onChange={(e) => patch({ company_name: e.target.value })}
          />
        </label>
      </div>

      <label className="field cff-form__address-field">
        <span className="field-label">Address</span>
        <input
          className={inputClass(customerReadOnly)}
          value={value.address}
          readOnly={customerReadOnly}
          onChange={(e) => patch({ address: e.target.value })}
        />
      </label>

      <div className="cff-form__grid2">
        <label className="field">
          <span className="field-label">Postal Code</span>
          <input
            className={inputClass(customerReadOnly)}
            value={value.postal_code}
            readOnly={customerReadOnly}
            onChange={(e) => patch({ postal_code: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">Territory Code</span>
          <input
            className={inputClass(customerReadOnly)}
            value={value.territory_code}
            readOnly={customerReadOnly}
            onChange={(e) => patch({ territory_code: e.target.value })}
          />
        </label>
      </div>

      <div className="cff-form__grid2">
        <label className="field">
          <span className="field-label">Contact Person</span>
          <input
            className={inputClass(customerReadOnly)}
            value={value.contact_person}
            readOnly={customerReadOnly}
            onChange={(e) => patch({ contact_person: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">Contact Number</span>
          <input
            className={inputClass(customerReadOnly)}
            value={value.contact_number}
            readOnly={customerReadOnly}
            onChange={(e) => patch({ contact_number: e.target.value })}
          />
        </label>
      </div>

      <h4 className="cff-form__subhead cff-form__subhead--items">b) Item Information (产品资料)</h4>
      <div className="cff-form__table-wrap">
        <table className="cff-form__items">
          <thead>
            <tr>
              <th className="cff-form__col-sn">S/N</th>
              <th className="cff-form__col-item">Item Code</th>
              <th className="cff-form__col-qty">Quantity</th>
              <th className="cff-form__col-uom">UOM</th>
            </tr>
          </thead>
          <tbody>
            {value.items.map((row, index) => (
              <tr key={index}>
                <td className="cff-form__sn">{index + 1}</td>
                <td>
                  {disabled ? (
                    <input className={inputClass(true)} value={row.item_code} readOnly />
                  ) : (
                    <CffAsyncTypeahead
                      value={row.item_code}
                      onChange={(code) =>
                        patch({ items: setItem(valueRef.current.items, index, { item_code: code }) })
                      }
                      onSelect={(opt) => {
                        const item = itemFromMeta(opt.meta);
                        patch({
                          items: setItem(valueRef.current.items, index, {
                            item_code: opt.value,
                            uom: item?.sales_uom || valueRef.current.items[index]?.uom || '',
                          }),
                        });
                      }}
                      search={searchItems}
                      placeholder="Search items"
                      ariaLabel={`Item code ${index + 1}`}
                    />
                  )}
                </td>
                <td>
                  <input
                    className="field-input"
                    value={row.quantity}
                    disabled={disabled}
                    onChange={(e) =>
                      patch({
                        items: setItem(valueRef.current.items, index, { quantity: e.target.value }),
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    className="field-input"
                    value={row.uom}
                    disabled={disabled}
                    onChange={(e) =>
                      patch({ items: setItem(valueRef.current.items, index, { uom: e.target.value }) })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="field cff-form__reason-field">
        <span className="field-label">Reason / 原因</span>
        <CffSearchableSelect
          value={value.reason}
          options={reasonOptions.map((r) => ({ value: r.value, label: r.label }))}
          onChange={(next) => patch({ reason: next })}
          disabled={disabled}
          placeholder={
            reasonOptions.length === 0
              ? 'Add reasons in Settings → CFF'
              : 'Search and select a reason'
          }
          ariaLabel="Reason"
        />
        {reasonOptions.length === 0 && !disabled ? (
          <span className="cff-form__hint">
            No reasons configured yet. Add them under DEM Settings → CFF.
          </span>
        ) : null}
      </label>

      <label className="field cff-form__reason-field">
        <span className="field-label">Internal remarks / 内部备注</span>
        <textarea
          className="field-input cff-form__reason-input"
          value={value.internal_remarks || ''}
          disabled={remarksDisabled}
          rows={3}
          placeholder="Enter internal remarks for reference"
          onChange={(e) => patch({ internal_remarks: e.target.value })}
          onBlur={() => onInternalRemarksBlur?.()}
        />
        <span className="cff-form__hint">
          For internal use only. These remarks are retained in the portal and are not exported to Excel or PDF.
        </span>
      </label>

      <div className="cff-form__grid3">
        <label className="field">
          <span className="field-label">Picker (optional)</span>
          <input
            className={inputClass(warehouseRolesDisabled)}
            value={value.picker || ''}
            readOnly={warehouseRolesDisabled}
            onChange={(e) => patch({ picker: e.target.value })}
            onBlur={() => onInternalRemarksBlur?.()}
          />
        </label>
        <label className="field">
          <span className="field-label">Packer (optional)</span>
          <input
            className={inputClass(warehouseRolesDisabled)}
            value={value.packer || ''}
            readOnly={warehouseRolesDisabled}
            onChange={(e) => patch({ packer: e.target.value })}
            onBlur={() => onInternalRemarksBlur?.()}
          />
        </label>
        <label className="field">
          <span className="field-label">Driver (optional)</span>
          <input
            className={inputClass(warehouseRolesDisabled)}
            value={value.driver || ''}
            readOnly={warehouseRolesDisabled}
            onChange={(e) => patch({ driver: e.target.value })}
            onBlur={() => onInternalRemarksBlur?.()}
          />
        </label>
      </div>

      <h4 className="cff-form__subhead">c) Invoice Information (订单资料)</h4>
      <div className="cff-form__invoice cff-form__invoice--meta">
        <label className="field">
          <span className="field-label">Invoice Date</span>
          <input
            type="date"
            className={inputClass(invoiceDateFieldLocked)}
            value={value.invoice_date}
            disabled={invoiceDateFieldLocked}
            readOnly={invoiceDateFieldLocked}
            onChange={(e) => patch({ invoice_date: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">Invoice Issued by</span>
          <input
            className={inputClass(invoiceIssuedByFieldLocked)}
            value={value.invoice_issued_by || ''}
            disabled={invoiceIssuedByFieldLocked}
            readOnly={invoiceIssuedByFieldLocked}
            placeholder={
              invoiceIssuedByFieldLocked
                ? undefined
                : 'Auto-filled from ledger, or enter manually'
            }
            onChange={(e) => patch({ invoice_issued_by: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">Submitted By (提交人)</span>
          <input className={inputClass(true)} value={submittedByDisplayName} readOnly />
        </label>
      </div>
    </div>
  );
}
